import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, TabContent, Table, TabPane, } from "reactstrap"
import { Box, IconButton, Tooltip } from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import { withTranslation } from "react-i18next"
import classnames from "classnames"
import moment from "moment"
import { isEmpty, map } from "lodash"
import Skeleton from "react-loading-skeleton"
import BaseDataTable from "../../components/Common/base-data-table"
import { buscarDispositivoLogs } from "store/auth/profile/actions"
import { apiBuscarNotificacoesUsuario } from "../../helpers/backend_helper"
import { Filters } from "../../common/data/definitions"

const describeDateTime = (d, t) => d
  ? moment(d).format("DD/MM/YY HH:mm")
  : t("cardapio.dispositivo.logsnotif.tipo.nd")

const describeLogTipo = (tipo, t) => {
  switch (tipo) {
    case "featured":
      return t("cardapio.dispositivo.logsnotif.tipo.featured")
    case "products":
      return t("cardapio.dispositivo.logsnotif.tipo.products")
    case "app_version":
      return t("cardapio.dispositivo.logsnotif.tipo.appversion")
    case "groups_availability":
      return t("cardapio.dispositivo.logsnotif.tipo.groups_availability")
    default:
      return tipo
        ? t("cardapio.dispositivo.logsnotif.tipo.outro")
        : t("cardapio.dispositivo.logsnotif.tipo.nd")
  }
}

const describeEnum = (value, prefix, t) => {
  if (!value) return null
  const key = `${prefix}.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

const DispositivoModal = props => {
  const { t, isOpen, dispositivo, onClose, dispositivoLogs, dispositivoLogsLoading } = props
  const [tab, setTab] = useState("logs")
  const [notificacoes, setNotificacoes] = useState([])
  const [notificacoesLoading, setNotificacoesLoading] = useState(false)
  // Rolar a tabela indica que o usuário está lendo os logs: pausa a atualização
  // automática até ele pedir explicitamente (botão atualizar ou voltar à aba).
  const [autoRefreshPausado, setAutoRefreshPausado] = useState(false)
  const refreshRef = useRef(null)

  const buscarLogs = () => {
    if (dispositivo?.id) props.buscarDispositivoLogs({ dispositivoId: dispositivo.id })
  }

  const atualizarLogs = () => {
    setAutoRefreshPausado(false)
    buscarLogs()
  }

  const abrirTabLogs = () => {
    setAutoRefreshPausado(false)
    setTab("logs")
  }

  const buscarNotificacoesDoDispositivo = () => {
    if (!dispositivo?.id) return
    setNotificacoesLoading(true)
    try {
      const filters = [
        {
          id: "dispositivos_ids",
          value: dispositivo.id,
          condition: Filters.arrIncludes,
        },
        { id: "notifica_baixa_bateria", value: true },
      ]
      if (dispositivo.idEmpresa) {
        filters.push({ id: "idEmpresa", value: [dispositivo.idEmpresa] })
      }
      apiBuscarNotificacoesUsuario({
        filters: JSON.stringify(filters),
        projection: JSON.stringify([
          "id",
          "created_at",
          "notificacao.titulo",
          "usuario.nome_completo",
          "lido",
          "data_lida",
          "tipo_notificacao",
        ]),
        sorting: '[{"id": "created_at", "desc":true}]',
      }).then(res => {
        setNotificacoes(res?.data || [])
      })
    } catch (e) {
      console.error(e)
      setNotificacoes([])
    } finally {
      setNotificacoesLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    setAutoRefreshPausado(false)
    buscarLogs()
    buscarNotificacoesDoDispositivo()
  }, [isOpen, dispositivo?.id])

  // Só atualiza sozinho enquanto a aba de logs estiver visível e o usuário não tiver
  // rolado a tabela.
  useEffect(() => {
    if (!isOpen || tab !== "logs" || autoRefreshPausado) return undefined

    refreshRef.current = setInterval(buscarLogs, 10000)
    return () => {
      if (refreshRef.current) {
        clearInterval(refreshRef.current)
        refreshRef.current = null
      }
    }
  }, [isOpen, dispositivo?.id, tab, autoRefreshPausado])

  const colunas = useMemo(
    () => [
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.codigo"),
        accessorKey: "id",
        size: 70,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.data"),
        accessorKey: "ultimaAtualizacao",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => describeDateTime(cell.getValue(), t),
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.origemapp"),
        accessorKey: "origem_app",
        size: 120,
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.tipo"),
        accessorKey: "tipo",
        size: 130,
        Cell: ({ cell }) => describeLogTipo(cell.getValue(), t),
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.operacao"),
        accessorKey: "operacao",
        size: 150,
        Cell: ({ cell }) =>
          describeEnum(cell.getValue(), "dispositivo.logs.operacao", t),
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.nivel"),
        accessorKey: "nivel",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const nivel = cell.getValue()
          if (!nivel) return null
          const color =
            nivel === "ERROR"
              ? "danger"
              : nivel === "WARNING"
              ? "warning"
              : "info"
          return (
            <Badge
              className={`font-size-12 badge-soft-${color}`}
              color={color}
              pill
            >
              {t(`dispositivo.logs.nivel.${nivel}`) || nivel}
            </Badge>
          )
        },
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.etapa"),
        accessorKey: "etapa",
        size: 150,
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.codigosuporte"),
        accessorKey: "codigo_suporte",
        size: 170,
        Cell: ({ cell }) => {
          const val = cell.getValue()
          if (!val) return null
          return (
            <div
              className="d-inline-flex align-items-center text-truncate"
              style={{ maxWidth: 200, cursor: "pointer" }}
              title={val}
              onClick={() => navigator.clipboard.writeText(val)}
            >
              <i className="bx bx-copy me-1" />
              {val}
            </div>
          )
        },
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.mensagemerros"),
        accessorKey: "erroMessage",
        size: 220,
        Cell: ({ cell, row }) => {
          const msg = cell.getValue()
          const hasError = row.original.erro
          if (!msg && !hasError) return null
          const truncated =
            msg && msg.length > 100 ? `${msg.substring(0, 100)}...` : msg
          return (
            <span title={msg || ""}>
              {hasError && (
                <i className="mdi mdi-alert-circle text-danger me-1" />
              )}
              {truncated}
            </span>
          )
        },
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.fabricante"),
        accessorKey: "fabricante_dispositivo",
        size: 120,
        Cell: ({ cell }) =>
          describeEnum(cell.getValue(), "dispositivo.logs.fabricante", t),
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.modelo"),
        accessorKey: "modelo_dispositivo",
        size: 120,
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.androidsdk"),
        accessorKey: "android_sdk",
        size: 110,
      },
      {
        header: t("cardapio.dispositivo.logsnotif.logs.coluna.detalhejson"),
        accessorKey: "detalhe_json",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ cell, row }) => {
          if (!cell.getValue()) return null
          return (
            <button
              className="btn btn-link p-0"
              onClick={() => row.toggleExpanded()}
            >
              <i
                className={`mdi mdi-${row.getIsExpanded() ? "chevron-up" : "chevron-down"} font-size-16`}
              />
            </button>
          )
        },
      },
    ],
    [t]
  )

  const describeType = tipo => {
    switch (tipo) {
      case "S":
        return "Sistema"
      case "P":
        return "Push Web"
      case "E":
        return "E-mail"
      default:
        return "N/D"
    }
  }

  const skeletonRow = i => (
    <tr key={`sk-${i}`}>
      <td>
        <Skeleton />
      </td>
      <td>
        <Skeleton />
      </td>
      <td>
        <Skeleton />
      </td>
      <td>
        <Skeleton />
      </td>
    </tr>
  )

  return (
    <Modal isOpen={ isOpen } toggle={ onClose } size="xl" centered scrollable>
      <ModalHeader toggle={ onClose }>
        { t("cardapio.dispositivo.logsnotif.titulo") }
      </ModalHeader>
      <ModalBody>
        <Nav tabs>
          <NavItem>
            <NavLink className={ classnames({ active: tab === "logs" }) }
                     onClick={ abrirTabLogs } style={ { cursor: "pointer" } }>
              { t("cardapio.dispositivo.logsnotif.tab.logs") }
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink className={ classnames({ active: tab === "notificacoes" }) }
                     onClick={ () => setTab("notificacoes") } style={ { cursor: "pointer" } }>
              { t("cardapio.dispositivo.logsnotif.tab.notificacoes") }
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={ tab } className="p-3">
          <TabPane tabId="logs">
            <BaseDataTable
              columns={colunas}
              data={dispositivoLogs || []}
              state={{ isLoading: dispositivoLogsLoading }}
              enablePagination={false}
              enableBottomToolbar={false}
              enableStickyHeader
              displayColumnDefOptions={{
                "mrt-row-expand": {
                  muiTableHeadCellProps: { sx: { display: "none" } },
                  muiTableBodyCellProps: { sx: { display: "none" } },
                  size: 0,
                  minSize: 0,
                },
              }}
              muiTableContainerProps={{
                sx: { maxHeight: 420 },
                onScroll: e => {
                  if (e.target.scrollTop > 0 && !autoRefreshPausado) {
                    setAutoRefreshPausado(true)
                  }
                }
              }}
              renderToolbarExtraActions={() => (
                <Tooltip title={t("cardapio.dispositivo.logsnotif.logs.atualizar")}>
                  <IconButton onClick={atualizarLogs}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
              initialState={{
                columnVisibility: {
                  etapa: false,
                  codigo_suporte: false,
                  fabricante_dispositivo: false,
                  android_sdk: false,
                },
              }}
              renderDetailPanel={({ row }) => {
                const json = row.original.detalhe_json
                if (!json) return null
                let formatted = json
                try {
                  formatted = JSON.stringify(JSON.parse(json), null, 2)
                } catch {}
                return (
                  <Box sx={{ p: 1 }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>
                        {t(
                          "cardapio.dispositivo.logsnotif.logs.coluna.detalhejson"
                        )}
                      </strong>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => navigator.clipboard.writeText(json)}
                      >
                        <i className="bx bx-copy me-1" />
                        {t(
                          "cardapio.dispositivo.logsnotif.logs.detalhe.copiar"
                        )}
                      </button>
                    </div>
                    <pre
                      style={{
                        maxHeight: 260,
                        overflow: "auto",
                        background: "#f4f4f4",
                        padding: 8,
                        borderRadius: 4,
                        fontSize: 12,
                        margin: 0,
                      }}
                    >
                      {formatted}
                    </pre>
                  </Box>
                )
              }}
            />
          </TabPane>
          <TabPane tabId="notificacoes">
            <div className="table-responsive">
              <Table className="table table-centered table-nowrap">
                <thead>
                  <tr>
                    <th>
                      {t("cardapio.dispositivo.logsnotif.notif.coluna.codigo")}
                    </th>
                    <th>
                      {t("cardapio.dispositivo.logsnotif.notif.coluna.data")}
                    </th>
                    <th>
                      {t("cardapio.dispositivo.logsnotif.notif.coluna.tipo")}
                    </th>
                    <th>
                      {t("cardapio.dispositivo.logsnotif.notif.coluna.titulo")}
                    </th>
                    <th>
                      {t("cardapio.dispositivo.logsnotif.notif.coluna.usuario")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {notificacoesLoading ? (
                    [0, 1, 2, 3].map(skeletonRow)
                  ) : isEmpty(notificacoes) ? (
                    <tr>
                      <td colSpan="4">
                        {t("cardapio.dispositivo.logsnotif.notif.vazio")}
                      </td>
                    </tr>
                  ) : (
                    map(notificacoes, (n, i) => (
                      <tr key={i}>
                        <td>{n.id}</td>
                        <td>{describeDateTime(n.created_at || n.data, t)}</td>
                        <td>{describeType(n.tipo_notificacao)}</td>
                        <td>{n.notificacao.titulo}</td>
                        <td>{n.usuario.nome_completo}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={ onClose }>
          { t("cardapio.dispositivo.logsnotif.botao.fechar") }
        </Button>
      </ModalFooter>
    </Modal>
  )
}

DispositivoModal.propTypes = {
  t: PropTypes.func,
  isOpen: PropTypes.bool.isRequired,
  dispositivo: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  dispositivoLogs: PropTypes.array,
  dispositivoLogsLoading: PropTypes.bool,
  buscarDispositivoLogs: PropTypes.func,
}

const mapStateToProps = state => {
  const { dispositivoLogs, dispositivoLogsLoading } = state.Profile
  return { dispositivoLogs, dispositivoLogsLoading }
}

export default withTranslation()(
  connect(mapStateToProps, { buscarDispositivoLogs })(DispositivoModal)
)
